/**
 * [상담 온보딩 — 계정 발급에 쓰는 순수 함수]  React·Firestore 를 모릅니다.
 * 시험: node tests/onboarding/onboarding.test.mjs
 *
 * 왜 필요한가
 * -----------
 * 예전 온보딩(신규 상담 등록 → 정식 등록)은 아이디·비밀번호·출결 PIN 을 전부 학부모 번호에서 계산했습니다.
 *   아이디 = 'imp_' + 번호 뒤 8자리, 초기 비밀번호 = 뒤 4자리 + '00', PIN = 뒤 4자리.
 * PIN 은 교직원 전원이 사용자 목록에서 보므로, 번호·아이디·PIN 중 하나만 알면 비밀번호가 나왔습니다.
 * 게다가 안내 문자의 접속 주소는 404 였고, 영단어 초기 세팅·상담 기록은 서버가 바꾼 문서 ID
 * ('imp_…' → 'impx…')가 아닌 바꾸기 전 ID 로 저장돼 학생 화면 어디에서도 보이지 않았습니다.
 *
 * 지금은
 * ------
 * 비밀번호는 서버(adminCreateUser, generatePassword)가 무작위로 만들고, 첫 로그인 때 바꾸게 합니다.
 * 이 파일의 randomInitialPassword 는 '옛 서버'(generatePassword 를 모르는 함수)가 아직 배포돼 있을 때
 * 그 서버가 그대로 쓰게 될 대체값입니다. 함수·앱 배포 순서가 바뀌어도 짐작 가능한 비밀번호가 나가지 않게 합니다.
 */

/* 확장자까지 적은 이유: 이 파일은 node 시험이 직접 불러옵니다(node 는 확장자 없는 경로를 못 찾습니다).
   웹 빌드(webpack)는 확장자가 있어도 같은 파일로 읽습니다. */
import { SYSTEM_URL } from '../constants.js';

/* 초기 비밀번호 글자 — 31자. 0·o·1·l·i 를 뺐습니다.
   데스크가 전화로 불러 주거나 학부모가 문자를 보고 옮겨 칠 때 헷갈리는 글자입니다.
   대문자도 뺐습니다. 휴대폰이 첫 글자를 대문자로 바꾸는 일이 잦아서입니다.
   서버(functions/authCore.js 의 generateInitialPassword)와 같은 글자 표를 씁니다 — 시험이 대조합니다. */
export const INITIAL_PW_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';
export const INITIAL_PW_LENGTH = 10;   // 31^10 ≈ 49비트

/* 치우침 없는 뽑기.
   바이트(0~255)를 31로 나눈 나머지를 그대로 쓰면 앞쪽 글자(0~7번)가 조금 더 자주 나옵니다(256 = 31×8 + 8).
   그래서 248(= 31×8) 이상인 바이트는 버리고 다시 뽑습니다. 남은 248개가 31글자에 8개씩 똑같이 나뉩니다. */
export const PW_BYTE_LIMIT = Math.floor(256 / INITIAL_PW_ALPHABET.length) * INITIAL_PW_ALPHABET.length;   // 248

/**
 * 무작위 초기 비밀번호를 만듭니다.
 * @param getRandomValues 난수 공급(시험에서는 정해 둔 바이트를 넣습니다). 기본값은 브라우저 암호 난수.
 * @param length 글자 수(기본 10)
 */
export function randomInitialPassword(getRandomValues = (a) => crypto.getRandomValues(a), length = INITIAL_PW_LENGTH) {
    let out = '';
    /* 버리는 바이트가 있어 한 번에 다 못 채울 수 있습니다. 다시 뽑습니다(평균 3% 정도만 버림).
       난수 공급이 고장 나 계속 큰 값만 주면 끝없이 돌지 않고 멈춰서 알립니다. */
    for (let round = 0; round < 50 && out.length < length; round += 1) {
        const buf = new Uint8Array(length * 2);
        getRandomValues(buf);
        for (let i = 0; i < buf.length && out.length < length; i += 1) {
            const b = buf[i];
            if (b >= PW_BYTE_LIMIT) continue;
            out += INITIAL_PW_ALPHABET[b % INITIAL_PW_ALPHABET.length];
        }
    }
    if (out.length < length) throw new Error('비밀번호를 만들 난수를 얻지 못했습니다. 새로고침 후 다시 시도해주세요.');
    return out;
}

/* 로그인 아이디 → Firebase Auth 이메일 앞부분이자 users 문서 ID.
   예: 'imp_12345678' → 'impx12345678' (밑줄도 영숫자가 아니라 x 가 됩니다).
   서버 toSafeId(functions/authCore.js — index.js 도 이것을 씀)와 글자 하나까지 같아야 합니다.
   시험이 functions 폴더의 정의를 직접 꺼내 대조합니다.
   같은 규칙의 다른 사본: 임페리얼 북스 로그인(imperial-homepage 저장소 books-order.html).
   하나를 바꾸면 전부 바꿉니다. (로그인 화면 App.js 와 사용자 관리 UserManager.js 는 사본을 두지 않고 이 함수를 씁니다) */
export const toSafeId = (raw) =>
    encodeURIComponent(String(raw || '')).replace(/[^a-zA-Z0-9]/g, 'x').toLowerCase();

/**
 * 신규 학생의 출결 PIN 을 정합니다 — 학부모 번호 뒤 4자리.
 * 다른 학생(가입 대기 제외)이 이미 그 PIN 을 쓰면 '' 를 돌려줍니다.
 * 예전 온보딩에는 중복 검사가 없어 형제·같은 끝번호 학생끼리 PIN 이 겹쳤습니다.
 * 기준은 사용자 관리 저장(UserManager)·출결 PIN 관리(AttendanceManager)와 같습니다.
 *
 * @param phone  학부모 번호(하이픈 있어도 됨)
 * @param users  교직원 화면의 users 목록 ({id, role, status, attendancePin})
 * @param selfId 새로 만들 학생의 문서 ID — 자기 자신과는 겹침으로 보지 않습니다
 */
export function pickPin(phone, users, selfId) {
    const digits = String(phone || '').replace(/[^0-9]/g, '');
    if (digits.length < 4) return '';
    const pin = digits.slice(-4);
    const taken = (Array.isArray(users) ? users : []).some(u =>
        u && u.role === 'student'
        && u.id !== selfId
        && u.status !== 'pending'
        && String(u.attendancePin ?? '') === pin);
    return taken ? '' : pin;
}

/**
 * 영어 진단 점수를 영단어 엔진 척도(CAT, 0~1000 정수)로 읽습니다. 맞지 않으면 null.
 * 예전 입력란 예시는 '예: 85' 였는데 엔진은 1000점 척도라, 100점 만점 점수를 넣은 학생은
 * 최하 난이도에서 시작하게 됩니다. 범위만은 여기서 막습니다(85 같은 값은 화면이 한 번 더 묻습니다).
 */
export function parseCatScore(raw) {
    const s = String(raw ?? '').trim();
    if (!/^\d{1,4}$/.test(s)) return null;
    const n = Number(s);
    return n >= 0 && n <= 1000 ? n : null;
}

/**
 * 계정 발급 안내 문자.
 * @param name          학생 이름
 * @param loginId       로그인 아이디(사용자 관리 화면에 보이는 'imp_…' 그대로 — 로그인은 toSafeId 로 바뀌어 동작합니다)
 * @param password      초기 비밀번호
 * @param englishReady  영단어 초기 세팅을 실제로 저장했을 때만 true — 저장 못 했는데 '세팅 완료'라고 쓰지 않습니다
 * @param firstLoginChange 첫 로그인 때 비밀번호 변경 화면이 실제로 뜨는가(새 서버가 발급했을 때 true).
 *                     옛 서버는 변경 표시를 세우지 않으므로, 그때 '바꾸는 화면이 나옵니다'라고 쓰면 거짓이 됩니다.
 */
export function buildWelcomeSms({ name, loginId, password, englishReady = false, firstLoginChange = true }) {
    const lines = [
        '[목동임페리얼학원]',
        '안녕하세요. 목동임페리얼학원입니다.',
        `${name} 학생의 상담 등록 및 계정 발급이 완료되었습니다.`,
        '',
        '[로그인 안내]',
        `- 접속 주소: ${SYSTEM_URL}`,
        `- 로그인 ID: ${loginId}`,
        `- 초기 비밀번호: ${password}`,
    ];
    const notes = [];
    if (firstLoginChange) notes.push('* 처음 로그인하시면 새 비밀번호로 바꾸는 화면이 나옵니다. 본인만 아는 비밀번호로 바꿔 주세요.');
    if (englishReady) notes.push('* 첫 등원 전 맞춤 단어장 세팅이 완료되어 있습니다.');
    return notes.length ? `${lines.join('\n')}\n\n${notes.join('\n')}` : lines.join('\n');
}

/**
 * 가입 안내 문자 (2026-11-05 — 상담 등록이 계정을 만들지 않고 '등록만' 하게 바뀐 뒤).
 * 아이디·비밀번호를 담지 않습니다. 학부모님·학생이 회원가입 화면에서 등록된 번호로 인증하면 바로 가입됩니다(functions/signupCore.js).
 * @param name            학생 이름
 * @param hasStudentPhone 학생 본인 번호도 등록했는가 — 없으면 학생 가입 안내 대신 '학생 번호를 알려 주시면' 안내
 * @param englishReady    영단어 초기 세팅을 실제로 저장했을 때만 true
 */
export function buildSignupGuideSms({ name, hasStudentPhone = false, englishReady = false }) {
    const lines = [
        '[목동임페리얼학원]',
        '안녕하세요. 목동임페리얼학원입니다.',
        `${name} 학생의 등록이 완료되었습니다.`,
        '',
        '[학습 시스템 가입 안내]',
        `- 접속 주소: ${SYSTEM_URL}`,
        '- [회원가입] → 가입 유형 \'학부모\' → 이 번호로 인증하시면 자녀 정보가 자동으로 채워집니다. 아이디·비밀번호만 정하시면 바로 가입됩니다.',
        hasStudentPhone
            ? '- 학생은 [회원가입] → \'학생\' → 학생 본인 휴대폰 번호로 인증해 가입합니다.'
            : '- 학생도 가입하려면 학생 휴대폰 번호를 학원에 알려 주세요.'
    ];
    if (englishReady) lines.push('', '* 첫 등원 전 맞춤 단어장 세팅이 완료되어 있습니다.');
    return lines.join('\n');
}
